import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/Input.js';
import { Button } from '../components/ui/Button.js';
import { toast } from '../components/ui/Toast.js';
import { useAuthStore } from '../stores/auth-store.js';
import { register } from '../lib/auth.js';
import { ApiError } from '../lib/api.js';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite') ?? undefined;

  const setUser = useAuthStore((s) => s.setUser);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      const { user } = await register(email, password, displayName, inviteToken);
      setUser(user);
      toast.success('Account created! Welcome to Ember.');
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-3xl">🔥</span>
          <h1 className="mt-2 text-2xl font-bold text-neutral-100">Ember</h1>
          <p className="mt-1 text-sm text-neutral-400">Private, encrypted calls</p>
        </div>

        <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-6 shadow-xl">
          <h2 className="mb-1 text-lg font-semibold text-neutral-100">Create account</h2>
          {inviteToken && (
            <p className="mb-4 text-xs text-ember-400 bg-ember-900/20 border border-ember-800/40 rounded-lg px-3 py-2">
              You're registering via an invite link.
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
            <Input
              label="Display name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              required
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              hint="At least 8 characters"
              required
            />

            {error && (
              <p className="rounded-lg bg-red-900/30 border border-red-800 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}

            <Button type="submit" isLoading={isLoading} className="w-full mt-1">
              Create account
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-neutral-500">
            Already have an account?{' '}
            <Link to="/login" className="text-ember-400 hover:text-ember-300 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
